<template>
  <div class="claim-settings-container h-full overflow-y-auto px-1 py-2">
    <!-- Header Section with Action Bar -->
    <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 pb-4 border-b border-gray-100">
      <div>
        <div class="flex items-center gap-2 mb-1">
          <v-icon color="#2563eb" size="20" class="w-8 h-8 rounded-lg bg-blue-50">mdi-car-speed-limiter</v-icon>
          <h2 class="text-xl font-bold text-gray-900 tracking-tight">Claim & Travel Settings</h2>
          <span
            class="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-blue-50 text-blue-700 border border-blue-200">
            {{ activeModesCount }} Active Modes
          </span>
        </div>
        <p class="text-xs text-gray-500 mb-0">
          Configure distance-based kilometer rates, flat trip amounts, and daily allowances for employee expense claims.
        </p>
      </div>

      <div class="flex items-center justify-end gap-2">
        <!-- Load Standard Defaults -->
        <v-btn outlined small color="primary" class="normal-case rounded-lg font-medium" @click="loadPresetDefaults"
          :disabled="isSaving">
          <v-icon left small>mdi-magic-staff</v-icon>
          Load Defaults
        </v-btn>

        <!-- Add Mode Button -->
        <v-btn small color="primary" class="normal-case rounded-lg font-medium white--text" depressed
          @click="openAddDialog" :disabled="isSaving">
          <v-icon left small>mdi-plus</v-icon>
          Add Mode
        </v-btn>

        <!-- Save Button -->
        <v-btn small color="#059669" class="normal-case rounded-lg font-semibold white--text" depressed
          :loading="isSaving" @click="saveClaimSettings">
          <v-icon left small>mdi-content-save-check</v-icon>
          Save
        </v-btn>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoadingConfig" class="py-16 flex flex-col items-center justify-center">
      <v-progress-circular indeterminate color="primary" size="40" width="3" class="mb-3"></v-progress-circular>
      <span class="text-xs text-gray-500 font-medium">Loading claim settings...</span>
    </div>

    <!-- Main Content -->
    <div v-else>
      <!-- Quick Overview KPI Cards -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 mb-6">
        <!-- Total Travel Modes -->
        <div class="bg-white rounded-xl border border-gray-200/80 p-4 shadow-2xs hover:shadow-xs transition-shadow">
          <div class="flex items-center justify-between mb-2">
            <span class="text-xs font-semibold text-gray-500 uppercase tracking-wider">Travel Modes</span>
            <v-icon size="16" color="#4f46e5" class="w-7 h-7 rounded-lg bg-indigo-50">mdi-road-variant</v-icon>
          </div>
          <div class="flex items-baseline gap-2">
            <span class="text-2xl font-black text-gray-900">{{ travelList.length }}</span>
            <span class="text-xs text-gray-500">configured</span>
          </div>
          <div class="mt-2 text-[11px] text-gray-500 flex items-center gap-1">
            <span class="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block"></span>
            {{ activeModesCount }} enabled for claims
          </div>
        </div>

        <!-- Distance-Wise KM Modes -->
        <div class="bg-white rounded-xl border border-gray-200/80 p-4 shadow-2xs hover:shadow-xs transition-shadow">
          <div class="flex items-center justify-between mb-2">
            <span class="text-xs font-semibold text-gray-500 uppercase tracking-wider">Per-KM Rates</span>
            <v-icon size="16" color="#059669" class="w-7 h-7 rounded-lg bg-emerald-50">mdi-map-marker-distance</v-icon>
          </div>
          <div class="flex items-baseline gap-2">
            <span class="text-2xl font-black text-gray-900">{{ perKmModesCount }}</span>
            <span class="text-xs text-emerald-600 font-medium">Distance-based</span>
          </div>
          <div class="mt-2 text-[11px] text-gray-500">
            Bike, Car & personal vehicles
          </div>
        </div>

        <!-- Flat / Fixed Limit Modes -->
        <div class="bg-white rounded-xl border border-gray-200/80 p-4 shadow-2xs hover:shadow-xs transition-shadow">
          <div class="flex items-center justify-between mb-2">
            <span class="text-xs font-semibold text-gray-500 uppercase tracking-wider">Fixed Max Limits</span>
            <v-icon size="16" color="#d97706" class="w-7 h-7 rounded-lg bg-amber-50">mdi-ticket-percent-outline</v-icon>
          </div>
          <div class="flex items-baseline gap-2">
            <span class="text-2xl font-black text-gray-900">{{ flatModesCount }}</span>
            <span class="text-xs text-amber-600 font-medium">Upper claim caps</span>
          </div>
          <div class="mt-2 text-[11px] text-gray-500">
            Bus, Train, Metro & Flight caps
          </div>
        </div>

        <!-- Daily Allowances -->
        <div class="bg-white rounded-xl border border-gray-200/80 p-4 shadow-2xs hover:shadow-xs transition-shadow">
          <div class="flex items-center justify-between mb-2">
            <span class="text-xs font-semibold text-gray-500 uppercase tracking-wider">Allowances</span>
            <v-icon size="16" color="#9333ea" class="w-7 h-7 rounded-lg bg-purple-50">mdi-cash-multiple</v-icon>
          </div>
          <div class="flex items-baseline gap-2">
            <span class="text-2xl font-black text-gray-900">{{ allowanceList.length }}</span>
            <span class="text-xs text-purple-600 font-medium">Types</span>
          </div>
          <div class="mt-2 text-[11px] text-gray-500">
            Per diem, outstation & food
          </div>
        </div>
      </div>

      <!-- Navigation Sub-Tabs -->
      <v-tabs v-model="activeSettingsTab" background-color="transparent" color="primary"
        class="mb-5 custom-settings-tabs" dense>
        <v-tab class="normal-case font-semibold text-xs tracking-wide">
          <v-icon left small>mdi-moped-outline</v-icon>
          Travel Modes ({{ travelList.length }})
        </v-tab>
        <v-tab class="normal-case font-semibold text-xs tracking-wide">
          <v-icon left small>mdi-cash-fast</v-icon>
          Daily Allowances ({{ allowanceList.length }})
        </v-tab>
        <v-tab class="normal-case font-semibold text-xs tracking-wide">
          <v-icon left small>mdi-shield-check-outline</v-icon>
          General Claim Policies
        </v-tab>
      </v-tabs>

      <!-- ==================== TAB 1: TRAVEL MODES & RATES ==================== -->
      <div v-show="activeSettingsTab === 0" class="space-y-4">
        <!-- Search & Filter Filter Bar -->
        <div
          class="bg-white p-3 rounded-xl border border-gray-200/80 flex flex-wrap items-center justify-between gap-3">
          <div class="flex items-center gap-1.5 flex-wrap">
            <span class="text-xs font-semibold text-gray-600 mr-2">Filter By:</span>
            <button type="button" v-for="filter in modeFilters" :key="filter.value"
              @click="selectedModeFilter = filter.value" :class="[
                'px-3 py-1 rounded-lg text-xs font-semibold transition-all',
                selectedModeFilter === filter.value
                  ? 'bg-blue-600 text-white shadow-2xs'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200/80'
              ]">
              {{ filter.label }}
              <span v-if="filter.count !== undefined" class="ml-1 opacity-80 text-[10px]">({{ filter.count }})</span>
            </button>
          </div>

          <div class="w-full sm:w-64">
            <v-text-field v-model="modeSearch" dense outlined hide-details placeholder="Search modes..."
              prepend-inner-icon="mdi-magnify" class="text-xs rounded-lg"></v-text-field>
          </div>
        </div>

        <!-- Travel Modes Grid of Cards -->
        <div v-if="filteredTravelModes.length > 0" class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <div v-for="(item, idx) in filteredTravelModes" :key="item.mode + '-' + idx" :class="[
            'mode-card bg-white rounded-xl border transition-all p-4 flex flex-col justify-between relative overflow-hidden',
            item.isActive !== false ? 'border-gray-200/90 shadow-2xs hover:shadow-xs' : 'border-dashed border-gray-300 bg-gray-50/70 opacity-80'
          ]">
            <!-- Top Row: Icon, Name & Status Switch -->
            <div>
              <div class="flex items-start justify-between gap-2 mb-3">
                <div class="flex items-center gap-3">
                  <v-icon class="w-10 h-10 rounded-xl"
                    :style="{ backgroundColor: getModeMeta(item.mode).bgColor, color: getModeMeta(item.mode).color }"
                    :color="getModeMeta(item.mode).color" size="22">
                    {{ getModeMeta(item.mode).icon }}
                  </v-icon>
                  <div>
                    <h4 class="font-bold text-gray-900 text-sm mb-0.5 flex items-center gap-1.5">
                      {{ formatModeName(item.mode) }}
                    </h4>
                    <span :class="[
                      'inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider',
                      getRateTypeBadgeClass(item)
                    ]">
                      <v-icon x-small :color="getRateTypeIconColor(item)">{{ getRateTypeIcon(item) }}</v-icon>
                      {{ getRateTypeLabel(item) }}
                    </span>
                  </div>
                </div>

                <!-- Active Toggle Switch -->
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on" class="pt-0.5">
                      <v-switch v-model="item.isActive" color="success" dense hide-details class="ma-0 pa-0"
                        @change="handleFieldChange"></v-switch>
                    </div>
                  </template>
                  <span>{{ item.isActive !== false ? 'Active for claims' : 'Inactive (disabled)' }}</span>
                </v-tooltip>
              </div>

              <!-- Description / Note -->
              <p v-if="item.description" class="text-xs text-gray-500 mb-3 line-clamp-2 italic">
                "{{ item.description }}"
              </p>

              <!-- Rate Metrics Pill Display -->
              <div class="bg-gray-50/80 rounded-lg p-2.5 mb-3 border border-gray-100">
                <!-- If Distance-Based Rate -->
                <div v-if="isPerKmMode(item)" class="flex items-baseline justify-between">
                  <div>
                    <span class="text-[11px] font-medium text-gray-500 block leading-tight">Rate per KM</span>
                    <span class="text-lg font-black text-emerald-700">₹ {{ formatAmount(item.pricePerKm) }}</span>
                    <span class="text-[10px] font-semibold text-gray-400"> / km</span>
                  </div>
                  <div class="text-right">
                    <span class="text-[10px] text-gray-500 block">Min Distance</span>
                    <span class="text-xs font-bold text-gray-800">{{ formatMinDistance(item.minDistance) }}</span>
                  </div>
                </div>

                <!-- If Flat Amount / Fixed Limit -->
                <div v-else-if="isFlatMode(item)" class="flex items-baseline justify-between">
                  <div>
                    <span class="text-[11px] font-medium text-gray-500 block leading-tight">Max Allowed Limit</span>
                    <span class="text-lg font-black text-amber-700">{{ formatFlatAmount(item) }}</span>
                    <span class="text-[10px] font-semibold text-gray-400"> / claim cap</span>
                  </div>
                  <div class="text-right">
                    <span class="text-[10px] text-gray-500 block">Pricing Type</span>
                    <span class="text-xs font-bold text-amber-800">Fixed Max Limit</span>
                  </div>
                </div>

                <!-- If Hybrid / Both -->
                <div v-else class="flex items-baseline justify-between">
                  <div>
                    <span class="text-[11px] font-medium text-gray-500 block leading-tight">Rate + Base</span>
                    <span class="text-base font-black text-blue-700">₹ {{ formatAmount(item.pricePerKm) }}/km</span>
                  </div>
                  <div class="text-right">
                    <span class="text-[10px] text-gray-500 block">Base Amount</span>
                    <span class="text-xs font-bold text-blue-800">₹ {{ formatAmount(item.flatAmount || 0) }}</span>
                  </div>
                </div>

                <!-- Min and Max Limits for distance modes -->
                <div v-if="isPerKmMode(item)" class="flex items-center justify-between pt-2 mt-2 border-t border-gray-200/60 text-[11px]">
                  <div class="text-gray-500">
                    Min Pay: <span class="font-bold text-gray-800">₹ {{ formatAmount(item.min || 0) }}</span>
                  </div>
                  <div class="text-gray-500">
                    Max Cap: <span class="font-bold text-gray-800">{{ formatMaxCap(item.max) }}</span>
                  </div>
                </div>
              </div>

              <!-- Receipt Requirement Badge -->
              <div class="flex items-center gap-1.5 text-xs text-gray-600 mb-3">
                <v-icon x-small :color="item.requiresReceipt ? '#2563eb' : '#94a3b8'">
                  {{ item.requiresReceipt ? 'mdi-file-document-check' : 'mdi-file-document-outline' }}
                </v-icon>
                <span class="text-[11px]">
                  {{ item.requiresReceipt ? 'Bill/Ticket receipt mandatory' : 'Bill receipt optional' }}
                </span>
              </div>
            </div>

            <!-- Bottom Actions: Edit & Delete -->
            <div class="pt-2 border-t border-gray-100 flex items-center justify-between">
              <span class="text-[11px] text-gray-400 font-mono">{{ item.mode }}</span>
              <div class="flex items-center gap-1">
                <v-btn icon x-small color="primary" @click="editMode(item, idx)" title="Edit Travel Mode">
                  <v-icon small>mdi-pencil-outline</v-icon>
                </v-btn>
                <v-btn icon x-small color="error" @click="confirmDeleteMode(idx)" title="Remove Mode">
                  <v-icon small>mdi-delete-outline</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State if no modes match filter -->
        <div v-else class="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <div class="w-12 h-12 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mx-auto mb-3">
            <v-icon color="#2563eb" size="24">mdi-car-off</v-icon>
          </div>
          <h4 class="font-bold text-gray-800 text-sm mb-1">No Travel Modes Found</h4>
          <p class="text-xs text-gray-500 max-w-sm mx-auto mb-4">
            No travel modes match your selected filter. Add a new travel mode or reset filters.
          </p>
          <v-btn small color="primary" class="normal-case rounded-lg" depressed @click="openAddDialog">
            <v-icon left small>mdi-plus</v-icon>
            Add Travel Mode
          </v-btn>
        </div>

        <!-- Interactive Rate Calculator & Payout Simulator (Embedded in Travel Modes Tab) -->
        <div class="bg-white rounded-xl border border-gray-200/80 p-5 mt-6">
          <div class="mb-4 pb-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 class="text-sm font-bold text-gray-900 mb-0.5 flex items-center gap-2">
                <v-icon small color="#2563eb">mdi-calculator-variant-outline</v-icon>
                Rate Calculator & Payout Simulator
              </h3>
              <p class="text-xs text-gray-500 mb-0">
                Test your configured travel rates and preview exactly what an employee will be reimbursed.
              </p>
            </div>
            <span
              class="px-2.5 py-1 rounded-full text-[11px] font-bold bg-blue-50 text-blue-700 border border-blue-200/60">
              Live Rate Testing
            </span>
          </div>

          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <!-- Left: Input Simulator Controls -->
            <div class="space-y-4">
              <div>
                <label class="block text-xs font-semibold text-gray-700 mb-1.5">Select Travel Mode</label>
                <v-select v-model="simMode" :items="travelList" item-text="mode" item-value="mode" outlined dense
                  hide-details class="rounded-lg">
                  <template v-slot:selection="{ item }">
                    <div class="flex items-center gap-2">
                      <v-icon small :color="getModeMeta(item.mode).color">{{ getModeMeta(item.mode).icon }}</v-icon>
                      <span class="text-xs font-bold">{{ formatModeName(item.mode) }}</span>
                      <span class="text-[10px] text-gray-500 font-mono">({{ getRateTypeLabel(item) }})</span>
                    </div>
                  </template>
                  <template v-slot:item="{ item }">
                    <div class="flex items-center gap-2 py-1">
                      <v-icon small :color="getModeMeta(item.mode).color">{{ getModeMeta(item.mode).icon }}</v-icon>
                      <span class="text-xs font-medium">{{ formatModeName(item.mode) }}</span>
                      <v-spacer></v-spacer>
                      <span class="text-[10px] font-bold text-emerald-700">
                        {{ formatSimRate(item) }}
                      </span>
                    </div>
                  </template>
                </v-select>
              </div>

              <!-- If distance mode, input distance -->
              <div v-if="selectedSimMode && isPerKmMode(selectedSimMode)">
                <label class="block text-xs font-semibold text-gray-700 mb-1.5">
                  Trip Distance (in Kilometers)
                </label>
                <div class="flex items-center gap-3">
                  <v-slider v-model="simDistance" min="1" max="200" step="1" hide-details class="flex-1"
                    color="primary"></v-slider>
                  <v-text-field v-model.number="simDistance" type="number" suffix="km" outlined dense hide-details
                    class="w-24 text-xs font-bold rounded-lg"></v-text-field>
                </div>
              </div>

              <!-- Quick distance presets -->
              <div v-if="selectedSimMode && isPerKmMode(selectedSimMode)" class="flex items-center gap-1.5">
                <span class="text-[11px] text-gray-500 mr-1">Quick:</span>
                <button type="button" v-for="d in [5, 10, 25, 50, 100]" :key="d" @click="simDistance = d"
                  class="px-2 py-0.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 text-[11px] font-medium">
                  {{ d }} km
                </button>
              </div>

              <!-- Include Daily Allowance checkbox -->
              <div class="pt-2">
                <v-checkbox v-model="simIncludeAllowance" label="Add Daily Allowance to calculation" dense hide-details
                  color="primary" class="ma-0 pa-0"></v-checkbox>
              </div>

              <div v-if="simIncludeAllowance">
                <v-select v-model="simSelectedAllowance" :items="allowanceList" item-text="type" item-value="amount"
                  outlined dense hide-details label="Select Allowance" class="rounded-lg text-xs">
                  <template v-slot:selection="{ item }">
                    <span class="text-xs">{{ item.type }} - ₹ {{ formatAmount(item.amount) }}</span>
                  </template>
                  <template v-slot:item="{ item }">
                    <span class="text-xs">{{ item.type }} - ₹ {{ formatAmount(item.amount) }}</span>
                  </template>
                </v-select>
              </div>
            </div>

            <!-- Right: Visual Receipt Breakdown -->
            <div
              class="bg-gradient-to-br from-blue-50 to-indigo-50/60 rounded-xl p-5 border border-blue-100 flex flex-col justify-between">
              <div>
                <div class="flex items-center justify-between mb-4 pb-2 border-b border-blue-200/60">
                  <span class="text-xs font-bold text-blue-950 uppercase tracking-wider">Estimated Payout</span>
                  <span class="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-600 text-white">Live Calculation</span>
                </div>

                <!-- Calculation Details -->
                <div class="space-y-2.5 text-xs text-gray-700 mb-4">
                  <div class="flex justify-between">
                    <span>Selected Mode:</span>
                    <span class="font-bold text-gray-900">{{ formatModeName(simMode) }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span>Pricing Method:</span>
                    <span class="font-bold text-indigo-700">
                      {{ selectedSimMode ? getRateTypeLabel(selectedSimMode) : '-' }}
                    </span>
                  </div>

                  <div v-if="selectedSimMode && isPerKmMode(selectedSimMode)" class="flex justify-between">
                    <span>Distance:</span>
                    <span class="font-medium">{{ formatSimDistanceBreakdown(selectedSimMode, simDistance) }}</span>
                  </div>

                  <div v-else-if="selectedSimMode && isFlatMode(selectedSimMode)" class="flex justify-between">
                    <span>Upper Cap Limit:</span>
                    <span class="font-bold text-amber-800">Max ₹ {{ formatAmount(selectedSimMode.flatAmount || selectedSimMode.max) }}</span>
                  </div>

                  <div v-if="simIncludeAllowance && simSelectedAllowance" class="flex justify-between">
                    <span>Daily Allowance:</span>
                    <span class="font-medium">₹ {{ formatAmount(simSelectedAllowance) }}</span>
                  </div>

                  <!-- Min / Max Cap note if applied -->
                  <div v-if="simBreakdownNote"
                    class="p-2 rounded bg-amber-50 border border-amber-200 text-amber-800 text-[11px] font-medium">
                    {{ simBreakdownNote }}
                  </div>
                </div>
              </div>

              <!-- Grand Total Display -->
              <div class="pt-4 border-t border-blue-200/80 flex items-baseline justify-between">
                <span class="text-sm font-bold text-gray-900">
                  {{ selectedSimMode && isFlatMode(selectedSimMode) ? 'Max Claim Allowed:' : 'Total Claim Amount:' }}
                </span>
                <span class="text-3xl font-black text-blue-900">
                  {{ selectedSimMode && isFlatMode(selectedSimMode) ? 'Max ' : '' }}₹ {{ formatAmount(calculatedSimPayout) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ==================== TAB 2: DAILY ALLOWANCES ==================== -->
      <div v-show="activeSettingsTab === 1" class="space-y-4">
        <div class="bg-white rounded-xl border border-gray-200/80 p-4">
          <div class="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
            <div>
              <h3 class="text-sm font-bold text-gray-900 mb-0.5">Configured Allowances</h3>
              <p class="text-xs text-gray-500 mb-0">Daily per diem rates and meal allowances for employees</p>
            </div>
            <v-btn small outlined color="primary" class="normal-case rounded-lg" @click="addAllowanceRow">
              <v-icon left small>mdi-plus</v-icon>
              Add
            </v-btn>
          </div>

          <div v-if="allowanceList.length > 0" class="space-y-3">
            <div v-for="(allowance, aIdx) in allowanceList" :key="'allowance-' + aIdx"
              class="p-3 bg-gray-50/70 rounded-xl border border-gray-200 flex flex-col md:flex-row md:items-center gap-3">
              <v-icon class="w-10 h-10 rounded-xl bg-purple-50" color="#9333ea" size="20">mdi-cash-fast</v-icon>

              <!-- Allowance Type Select -->
              <div class="flex-1">
                <v-autocomplete v-model="allowance.type" :items="predefinedAllowanceTypes" item-text="text"
                  item-value="value" label="Allowance Type" outlined dense hide-details
                  class="text-xs bg-white rounded-lg" @change="handleFieldChange"></v-autocomplete>
              </div>

              <!-- Amount Input -->
              <div class="w-full md:w-48">
                <v-text-field v-model.number="allowance.amount" label="Daily Amount (₹)" prefix="₹" type="number"
                  min="0" outlined dense hide-details class="text-xs bg-white rounded-lg"
                  @input="handleFieldChange"></v-text-field>
              </div>

              <!-- Actions -->
              <div class="flex items-center justify-end">
                <v-btn icon small color="error" @click="removeAllowanceRow(aIdx)" title="Remove Allowance">
                  <v-icon small>mdi-delete-outline</v-icon>
                </v-btn>
              </div>
            </div>
          </div>

          <div v-else class="text-center py-6 text-gray-400">
            <v-icon size="32" color="#cbd5e1" class="mb-2">mdi-cash-remove</v-icon>
            <div class="text-xs">No daily allowances configured yet</div>
          </div>
        </div>
      </div>

      <!-- ==================== TAB 3: GENERAL CLAIM POLICIES ==================== -->
      <div v-show="activeSettingsTab === 2" class="space-y-4">
        <div class="bg-white rounded-xl border border-gray-200/80 p-5 space-y-5">
          <div>
            <h3 class="text-sm font-bold text-gray-900 mb-0.5">Claim Policies & Validation Rules</h3>
            <p class="text-xs text-gray-500 mb-0">Enforce receipt attachments, maximum daily caps, GPS calculation, and
              submission windows</p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <!-- Auto-Calculate GPS Distance -->
            <div class="p-4 rounded-xl border border-gray-200 bg-gray-50/50 flex items-start justify-between gap-4">
              <div>
                <h4 class="text-sm font-bold text-gray-900 mb-1 flex items-center gap-1.5">
                  <v-icon small color="primary">mdi-crosshairs-gps</v-icon>
                  Auto-Calculate Distance with GPS
                </h4>
                <p class="text-xs text-gray-500 mb-0">
                  Automatically pre-fill kilometers from the field employee's location tracking history during their
                  shift.
                </p>
              </div>
              <v-switch v-model="claimPolicies.autoGpsDistance" color="primary" dense hide-details class="ma-0 pa-0"
                @change="handleFieldChange"></v-switch>
            </div>

            <!-- Mandatory Bill Receipt Threshold -->
            <div class="p-4 rounded-xl border border-gray-200 bg-gray-50/50 flex items-start justify-between gap-4">
              <div>
                <h4 class="text-sm font-bold text-gray-900 mb-1 flex items-center gap-1.5">
                  <v-icon small color="primary">mdi-receipt-text-check-outline</v-icon>
                  Require Bills Above Amount
                </h4>
                <p class="text-xs text-gray-500 mb-0">
                  Force employee to attach invoice/ticket receipt whenever claim exceeds this threshold.
                </p>
              </div>
              <div class="w-32 shrink-0">
                <v-text-field v-model.number="claimPolicies.receiptThreshold" prefix="₹" type="number" outlined dense
                  hide-details class="text-xs bg-white rounded-lg font-bold" @input="handleFieldChange"></v-text-field>
              </div>
            </div>

            <!-- Maximum Claim Per Day Cap -->
            <div class="p-4 rounded-xl border border-gray-200 bg-gray-50/50 flex items-start justify-between gap-4">
              <div>
                <h4 class="text-sm font-bold text-gray-900 mb-1 flex items-center gap-1.5">
                  <v-icon small color="primary">mdi-cash-lock</v-icon>
                  Max Claim Amount Per Day
                </h4>
                <p class="text-xs text-gray-500 mb-0">
                  Upper limit an employee can claim on any single calendar day across all travel modes.
                </p>
              </div>
              <div class="w-32 shrink-0">
                <v-text-field v-model.number="claimPolicies.maxDailyClaimCap" prefix="₹" type="number" outlined dense
                  hide-details class="text-xs bg-white rounded-lg font-bold" @input="handleFieldChange"></v-text-field>
              </div>
            </div>

            <!-- Manager Approval Required -->
            <div class="p-4 rounded-xl border border-gray-200 bg-gray-50/50 flex items-start justify-between gap-4">
              <div>
                <h4 class="text-sm font-bold text-gray-900 mb-1 flex items-center gap-1.5">
                  <v-icon small color="primary">mdi-account-check-outline</v-icon>
                  Require Manager Approval
                </h4>
                <p class="text-xs text-gray-500 mb-0">
                  Submitted claims require line-manager review and approval before being sent to Accounts for payment.
                </p>
              </div>
              <v-switch v-model="claimPolicies.requireManagerApproval" color="primary" dense hide-details
                class="ma-0 pa-0" @change="handleFieldChange"></v-switch>
            </div>

            <!-- Claim Submission Window -->
            <div class="p-4 rounded-xl border border-gray-200 bg-gray-50/50 flex items-start justify-between gap-4">
              <div>
                <h4 class="text-sm font-bold text-gray-900 mb-1 flex items-center gap-1.5">
                  <v-icon small color="primary">mdi-calendar-clock</v-icon>
                  Claim Submission Window (Days)
                </h4>
                <p class="text-xs text-gray-500 mb-0">
                  Maximum number of days within which an employee must submit claims after incurring expense.
                </p>
              </div>
              <div class="w-32 shrink-0">
                <v-text-field v-model.number="claimPolicies.submissionWindowDays" suffix="days" type="number" outlined
                  dense hide-details class="text-xs bg-white rounded-lg font-bold"
                  @input="handleFieldChange"></v-text-field>
              </div>
            </div>

            <!-- Allow Past Date Claims -->
            <div class="p-4 rounded-xl border border-gray-200 bg-gray-50/50 flex items-start justify-between gap-4">
              <div>
                <h4 class="text-sm font-bold text-gray-900 mb-1 flex items-center gap-1.5">
                  <v-icon small color="primary">mdi-history</v-icon>
                  Allow Past Date Claims
                </h4>
                <p class="text-xs text-gray-500 mb-0">
                  Allow field staff to backdate expenses when submitting bills for previous days/trips.
                </p>
              </div>
              <v-switch v-model="claimPolicies.allowPastDateClaims" color="primary" dense hide-details class="ma-0 pa-0"
                @change="handleFieldChange"></v-switch>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ==================== DIALOG: ADD / EDIT TRAVEL MODE ==================== -->
    <v-dialog v-model="modeDialog" max-width="600px" persistent>
      <v-card class="rounded-md overflow-hidden px-4">
        <div class="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div class="flex items-center gap-2.5">
            <v-icon class="w-8 h-8 rounded-lg bg-blue-100" color="#2563eb" size="20">{{ isEditingMode ? 'mdi-pencil' :
              'mdi-plus' }}</v-icon>
            <!-- <div class="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-blue-700">
            </div> -->
            <div>
              <h3 class="text-sm font-bold text-gray-900 mb-0">
                {{ isEditingMode ? 'Edit Travel Mode' : 'Add New Travel Mode' }}
              </h3>
              <p class="text-[11px] text-gray-500 mb-0">Configure pricing rate and validation rules</p>
            </div>
          </div>
          <v-btn icon small @click="closeModeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="p-5 space-y-4">
          <!-- 1. Travel Mode Type Selection -->
          <div>
            <label class="block text-xs font-semibold text-gray-800 mb-1.5">
              <span class="text-red-500">*</span> Travel Mode
            </label>
            <v-autocomplete v-model="modeForm.mode" :items="availableModeOptions" item-text="text" item-value="value"
              outlined dense hide-details placeholder="Select or enter vehicle / mode" class="rounded-lg text-xs"
              @change="handleModeSelect">
              <template v-slot:selection="{ item }">
                <div class="flex items-center gap-2">
                  <v-icon small :color="item.color || '#2563eb'">{{ item.icon || 'mdi-car' }}</v-icon>
                  <span class="text-xs font-bold">{{ item.text }}</span>
                </div>
              </template>
              <template v-slot:item="{ item }">
                <div class="flex items-center gap-2 py-1">
                  <v-icon small :color="item.color || '#2563eb'">{{ item.icon || 'mdi-car' }}</v-icon>
                  <span class="text-xs font-medium">{{ item.text }}</span>
                </div>
              </template>
            </v-autocomplete>
          </div>

          <!-- 2. Rate Calculation Method (Per KM vs Flat vs Hybrid) -->
          <div>
            <label class="block text-xs font-semibold text-gray-800 mb-1.5">
              <span class="text-red-500">*</span> Reimbursement Method
            </label>
            <div class="grid grid-cols-2 gap-3">
              <!-- Distance-Based Option -->
              <div @click="modeForm.rateType = 'PER_KM'" :class="[
                'p-3 rounded-xl border-2 cursor-pointer transition-all flex flex-col justify-between',
                modeForm.rateType === 'PER_KM'
                  ? 'border-emerald-600 bg-emerald-50/50 shadow-2xs'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              ]">
                <div class="flex items-center justify-between mb-1.5">
                  <v-icon :color="modeForm.rateType === 'PER_KM' ? '#059669' : '#64748b'" size="20">
                    mdi-map-marker-distance
                  </v-icon>
                  <v-icon v-if="modeForm.rateType === 'PER_KM'" color="#059669" small>
                    mdi-check-circle
                  </v-icon>
                </div>
                <div>
                  <h4 class="text-xs font-bold text-gray-900 mb-0.5">Distance Wise (₹ / KM)</h4>
                  <p class="text-[10px] text-gray-500 mb-0">Payout calculated as Kilometers × Rate/km (Bike, Car, etc.)
                  </p>
                </div>
              </div>

              <!-- Flat Amount Option -->
              <div @click="modeForm.rateType = 'FLAT'" :class="[
                'p-3 rounded-xl border-2 cursor-pointer transition-all flex flex-col justify-between',
                modeForm.rateType === 'FLAT'
                  ? 'border-amber-600 bg-amber-50/50 shadow-2xs'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              ]">
                <div class="flex items-center justify-between mb-1.5">
                  <v-icon :color="modeForm.rateType === 'FLAT' ? '#d97706' : '#64748b'" size="20">
                    mdi-ticket-percent-outline
                  </v-icon>
                  <v-icon v-if="modeForm.rateType === 'FLAT'" color="#d97706" small>
                    mdi-check-circle
                  </v-icon>
                </div>
                <div>
                  <h4 class="text-xs font-bold text-gray-900 mb-0.5">Fixed Limit / Max Amount (₹)</h4>
                  <p class="text-[10px] text-gray-500 mb-0">Upper limit per trip/claim (Bus, Metro, Train, Flight)</p>
                </div>
              </div>
            </div>
          </div>

          <!-- 3. Dynamic Pricing Fields -->
          <!-- IF PER_KM: Show Price per km, Min distance, Min/Max caps -->
          <div v-if="modeForm.rateType === 'PER_KM'"
            class="p-3.5 bg-emerald-50/40 rounded-xl border border-emerald-100 space-y-3">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label class="block text-xs font-bold text-emerald-950 mb-1">
                  <span class="text-red-500">*</span> Rate Per Kilometer
                </label>
                <v-text-field v-model.number="modeForm.pricePerKm" prefix="₹" suffix="/ km" type="number" min="0"
                  step="0.5" outlined dense hide-details class="text-xs bg-white rounded-lg font-bold"
                  placeholder="e.g. 5.5"></v-text-field>
              </div>

              <div>
                <label class="block text-xs font-bold text-gray-700 mb-1">
                  Minimum Distance (km)
                </label>
                <v-text-field v-model.number="modeForm.minDistance" suffix="km" type="number" min="0" outlined dense
                  hide-details class="text-xs bg-white rounded-lg" placeholder="e.g. 2"></v-text-field>
              </div>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label class="block text-xs font-semibold text-gray-700 mb-1">
                  Minimum Payout Guarantee (₹)
                </label>
                <v-text-field v-model.number="modeForm.min" prefix="₹" type="number" min="0" outlined dense hide-details
                  class="text-xs bg-white rounded-lg" placeholder="e.g. 25"></v-text-field>
              </div>

              <div>
                <label class="block text-xs font-semibold text-gray-700 mb-1">
                  Maximum Cap Limit (₹)
                </label>
                <v-text-field v-model.number="modeForm.max" prefix="₹" type="number" min="0" outlined dense hide-details
                  class="text-xs bg-white rounded-lg" placeholder="Optional, e.g. 500"></v-text-field>
              </div>
            </div>
          </div>

          <!-- IF FLAT / FIXED: Show Maximum Allowed Limit Cap -->
          <div v-else class="p-3.5 bg-amber-50/40 rounded-xl border border-amber-100 space-y-3">
            <div>
              <label class="block text-xs font-bold text-amber-950 mb-1">
                <span class="text-red-500">*</span> Maximum Allowed Limit / Cap (₹)
              </label>
              <v-text-field v-model.number="modeForm.flatAmount" prefix="₹" type="number" min="0" outlined dense
                hide-details class="text-xs bg-white rounded-lg font-bold" placeholder="e.g. 500"></v-text-field>
              <span class="text-[11px] text-amber-800 font-medium mt-1 block">
                Employees can claim their actual ticket or receipt amount up to this maximum limit.
              </span>
            </div>
          </div>

          <!-- Description / Guideline Note -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 mb-1">
              Description / Employee Instructions (Optional)
            </label>
            <v-text-field v-model.trim="modeForm.description" outlined dense hide-details
              placeholder="e.g. For metro rail or non-AC local train tickets only"
              class="text-xs rounded-lg"></v-text-field>
          </div>

          <!-- Additional Toggles -->
          <div class="pt-2 border-t border-gray-100 flex flex-col gap-2">
            <div class="flex items-center justify-between">
              <div>
                <span class="text-xs font-semibold text-gray-800 block">Require Bill / Ticket Receipt</span>
                <span class="text-[11px] text-gray-500">Employee must attach photo or PDF ticket</span>
              </div>
              <v-switch v-model="modeForm.requiresReceipt" color="primary" dense hide-details
                class="ma-0 pa-0"></v-switch>
            </div>

            <div class="flex items-center justify-between">
              <div>
                <span class="text-xs font-semibold text-gray-800 block">Active Status</span>
                <span class="text-[11px] text-gray-500">Available for employees to select in app</span>
              </div>
              <v-switch v-model="modeForm.isActive" color="success" dense hide-details class="ma-0 pa-0"></v-switch>
            </div>
          </div>
        </v-card-text>

        <!-- Dialog Footer Actions -->
        <div class="p-3 border-t border-gray-100 flex items-center justify-end gap-2">
          <v-btn outlined small color="grey darken-2" class="normal-case rounded-lg font-medium"
            @click="closeModeDialog">
            Cancel
          </v-btn>
          <v-btn small color="primary" class="normal-case rounded-lg font-semibold white--text" depressed
            @click="submitModeForm" :disabled="!isModeFormValid">
            {{ isEditingMode ? 'Update Mode' : 'Add Mode' }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { getClaimSettings, saveClaimSettings } from '@/app/services/claim'

export default {
  name: 'ClaimSettings',
  props: {
    receivedData: {
      type: Object,
      default: () => ({})
    },
    orgData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      activeSettingsTab: 0,
      isLoadingConfig: false,
      isSaving: false,
      selectedModeFilter: 'ALL',
      modeSearch: '',

      // Travel Modes Array
      travelList: [],

      // Allowances Array
      allowanceList: [],

      // General Policies
      claimPolicies: {
        autoGpsDistance: true,
        receiptThreshold: 500,
        maxDailyClaimCap: 5000,
        requireManagerApproval: true,
        submissionWindowDays: 30,
        allowPastDateClaims: true,
        maxPastDaysAllowed: 60
      },

      // Simulator data
      simMode: 'BIKE',
      simDistance: 15,
      simIncludeAllowance: false,
      simSelectedAllowance: null,

      // Modal Dialog
      modeDialog: false,
      isEditingMode: false,
      editingIndex: -1,
      modeForm: {
        mode: '',
        rateType: 'PER_KM',
        pricePerKm: 5,
        flatAmount: null,
        minDistance: 0,
        min: 0,
        max: null,
        description: '',
        requiresReceipt: false,
        isActive: true
      },

      // Predefined Standard Modes Catalog
      standardCatalog: [
        { text: 'Bike / 2-Wheeler', value: 'BIKE', icon: 'mdi-motorbike', color: '#059669', bgColor: '#ecfdf5', defaultRate: 4.5, defaultType: 'PER_KM' },
        { text: 'Car / 4-Wheeler', value: 'CAR', icon: 'mdi-car', color: '#2563eb', bgColor: '#eff6ff', defaultRate: 9, defaultType: 'PER_KM' },
        { text: 'Auto Rickshaw', value: 'AUTORICKSHAW', icon: 'mdi-moped', color: '#d97706', bgColor: '#fffbeb', defaultRate: 12, defaultType: 'PER_KM' },
        { text: 'Taxi / Cab', value: 'TAXI', icon: 'mdi-taxi', color: '#4f46e5', bgColor: '#eef2ff', defaultRate: 15, defaultType: 'PER_KM' },
        { text: 'Bus', value: 'BUS', icon: 'mdi-bus', color: '#7c3aed', bgColor: '#f5f3ff', defaultFlat: 50, defaultType: 'FLAT' },
        { text: 'Train / Metro', value: 'TRAIN', icon: 'mdi-train', color: '#0891b2', bgColor: '#ecfeff', defaultFlat: 70, defaultType: 'FLAT' },
        { text: 'Flight / Air', value: 'FLIGHT', icon: 'mdi-airplane', color: '#0284c7', bgColor: '#f0f9ff', defaultFlat: 3500, defaultType: 'FLAT' },
        { text: 'Fuel Bill Reimbursement', value: 'FUEL_BILL', icon: 'mdi-gas-station', color: '#dc2626', bgColor: '#fef2f2', defaultRate: 1, defaultType: 'PER_KM' },
        { text: 'Other Travel Expenses', value: 'OTHER_EXPENSES', icon: 'mdi-receipt-outline', color: '#475569', bgColor: '#f8fafc', defaultFlat: 100, defaultType: 'FLAT' }
      ],

      // Predefined Allowance Types
      predefinedAllowanceTypes: [
        { text: 'In-Station Work Allowance', value: 'IN_STATION_WORK' },
        { text: 'Same Day Outstation Travel', value: 'SAME_DAY_TRAVEL' },
        { text: 'Overnight Travel Stay', value: 'OVERNIGHT_TRAVEL' },
        { text: 'Food / Meal Allowance', value: 'FOOD_ALLOWANCE' },
        { text: 'Daily Allowance (DA)', value: 'DEARNESS_ALLOWANCE' },
        { text: 'Travel Allowance', value: 'TRAVEL_ALLOWANCE' },
        { text: 'Miscellaneous Allowance', value: 'MISCELLANEOUS' }
      ]
    }
  },
  computed: {
    modeFilters() {
      return [
        { label: 'All Modes', value: 'ALL', count: this.travelList.length },
        { label: 'Per KM', value: 'PER_KM', count: this.perKmModesCount },
        { label: 'Flat Fares', value: 'FLAT', count: this.flatModesCount },
        { label: 'Active', value: 'ACTIVE', count: this.activeModesCount }
      ]
    },
    activeModesCount() {
      return this.travelList.filter((m) => m.isActive !== false).length
    },
    perKmModesCount() {
      return this.travelList.filter((m) => this.isPerKmMode(m)).length
    },
    flatModesCount() {
      return this.travelList.filter((m) => this.isFlatMode(m)).length
    },
    availableModeOptions() {
      return this.standardCatalog
    },
    filteredTravelModes() {
      return this.travelList.filter((item) => {
        // Filter by tab
        if (this.selectedModeFilter === 'PER_KM' && !this.isPerKmMode(item)) return false
        if (this.selectedModeFilter === 'FLAT' && !this.isFlatMode(item)) return false
        if (this.selectedModeFilter === 'ACTIVE' && item.isActive === false) return false

        // Filter by search
        if (this.modeSearch && this.modeSearch.trim()) {
          const q = this.modeSearch.toLowerCase().trim()
          const modeName = this.formatModeName(item.mode).toLowerCase()
          return modeName.includes(q) || (item.mode && item.mode.toLowerCase().includes(q))
        }
        return true
      })
    },
    selectedSimMode() {
      return this.travelList.find((m) => m.mode === this.simMode) || this.travelList[0] || null
    },
    calculatedSimPayout() {
      if (!this.selectedSimMode) return 0
      let payout = 0

      if (this.isPerKmMode(this.selectedSimMode)) {
        const rate = Number(this.selectedSimMode.pricePerKm) || 0
        const dist = Number(this.simDistance) || 0
        payout = rate * dist

        // Apply min guarantee
        if (this.selectedSimMode.min && payout < Number(this.selectedSimMode.min)) {
          payout = Number(this.selectedSimMode.min)
        }
        // Apply max cap
        if (this.selectedSimMode.max && payout > Number(this.selectedSimMode.max)) {
          payout = Number(this.selectedSimMode.max)
        }
      } else {
        // For fixed/flat modes, flat amount and maximum amount are the same cap
        payout = Number(this.selectedSimMode.max || this.selectedSimMode.flatAmount || 0)
      }

      if (this.simIncludeAllowance && this.simSelectedAllowance) {
        payout += Number(this.simSelectedAllowance) || 0
      }

      return Math.round(payout * 100) / 100
    },
    simBreakdownNote() {
      if (!this.selectedSimMode) return ''
      if (this.isPerKmMode(this.selectedSimMode)) {
        const raw = (Number(this.selectedSimMode.pricePerKm) || 0) * (Number(this.simDistance) || 0)
        if (this.selectedSimMode.min && raw < Number(this.selectedSimMode.min)) {
          return `Minimum payout guarantee of ₹${this.selectedSimMode.min} applied (raw was ₹${raw}).`
        }
        if (this.selectedSimMode.max && raw > Number(this.selectedSimMode.max)) {
          return `Maximum payout cap of ₹${this.selectedSimMode.max} applied (raw was ₹${raw}).`
        }
      }
      return ''
    },
    isModeFormValid() {
      if (!this.modeForm.mode) return false
      if (this.modeForm.rateType === 'PER_KM') {
        return this.modeForm.pricePerKm !== null && this.modeForm.pricePerKm >= 0
      } else {
        return this.modeForm.flatAmount !== null && this.modeForm.flatAmount >= 0
      }
    }
  },
  watch: {
    receivedData: {
      handler(val) {
        if (!this.travelList.length && val) {
          this.initializeFromProps(val)
        }
      },
      deep: true
    }
  },
  mounted() {
    this.fetchClaimSettings()
  },
  methods: {
    async fetchClaimSettings() {
      this.isLoadingConfig = true
      try {
        const res = await getClaimSettings()
        const data = res?.data?.data
        if (data && Array.isArray(data.travelModes) && data.travelModes.length > 0) {
          this.travelList = data.travelModes.map((item) => ({
            mode: item.mode || '',
            rateType: item.rateType || (item.flatAmount && !item.pricePerKm ? 'FLAT' : 'PER_KM'),
            pricePerKm: item.pricePerKm !== undefined && item.pricePerKm !== null ? item.pricePerKm : 0,
            flatAmount: item.flatAmount || (item.rateType === 'FLAT' ? item.min : 0),
            minDistance: item.minDistance || 0,
            min: item.min || 0,
            max: item.max || null,
            description: item.description || '',
            requiresReceipt: item.requiresReceipt === true,
            isActive: item.isActive !== false
          }))

          if (Array.isArray(data.allowances) && data.allowances.length > 0) {
            this.allowanceList = data.allowances.map((a) => ({
              type: a.type || '',
              amount: a.amount !== undefined ? a.amount : 0,
              requiresReceipt: a.requiresReceipt === true,
              description: a.description || '',
              isActive: a.isActive !== false
            }))
          }

          if (data.generalPolicies) {
            this.claimPolicies = {
              ...this.claimPolicies,
              ...data.generalPolicies
            }
          }

          if (this.travelList.length > 0) {
            this.simMode = this.travelList[0].mode
          }
        } else {
          this.initializeFromProps(this.receivedData)
        }
      } catch (error) {
        console.warn('Could not fetch dedicated claim settings, falling back to props:', error)
        this.initializeFromProps(this.receivedData)
      } finally {
        this.isLoadingConfig = false
      }
    },

    initializeFromProps(data) {
      if (!data) return

      // Load travel modes
      if (Array.isArray(data.travelModes) && data.travelModes.length > 0) {
        this.travelList = data.travelModes.map((item) => ({
          mode: item.mode || '',
          rateType: item.rateType || (item.flatAmount && !item.pricePerKm ? 'FLAT' : 'PER_KM'),
          pricePerKm: item.pricePerKm !== undefined && item.pricePerKm !== null ? item.pricePerKm : 0,
          flatAmount: item.flatAmount || (item.rateType === 'FLAT' ? item.min : 0),
          minDistance: item.minDistance || 0,
          min: item.min || 0,
          max: item.max || null,
          description: item.description || '',
          requiresReceipt: item.requiresReceipt === true,
          isActive: item.isActive !== false
        }))
      } else {
        // Populate standard default travel modes if none exists
        this.loadPresetDefaults(false)
      }

      // Load allowances
      if (Array.isArray(data.orgAllowances) && data.orgAllowances.length > 0) {
        this.allowanceList = data.orgAllowances.map((a) => ({
          type: a.type || '',
          amount: a.amount !== undefined ? a.amount : 0
        }))
      } else {
        this.allowanceList = [
          { type: 'IN_STATION_WORK', amount: 150 },
          { text: 'SAME_DAY_TRAVEL', type: 'SAME_DAY_TRAVEL', amount: 300 },
          { type: 'FOOD_ALLOWANCE', amount: 200 }
        ]
      }

      // Set default simulator mode
      if (this.travelList.length > 0) {
        this.simMode = this.travelList[0].mode
      }
    },

    loadPresetDefaults(markDirty = true) {
      this.travelList = [
        {
          mode: 'BIKE',
          rateType: 'PER_KM',
          pricePerKm: 4.5,
          flatAmount: 0,
          minDistance: 1,
          min: 20,
          max: 600,
          description: 'Standard reimbursement for field staff two-wheelers',
          requiresReceipt: false,
          isActive: true
        },
        {
          mode: 'CAR',
          rateType: 'PER_KM',
          pricePerKm: 9.0,
          flatAmount: 0,
          minDistance: 5,
          min: 100,
          max: 1800,
          description: 'Official four-wheeler travel (Fuel and maintenance inclusive)',
          requiresReceipt: false,
          isActive: true
        },
        {
          mode: 'AUTORICKSHAW',
          rateType: 'PER_KM',
          pricePerKm: 12.0,
          flatAmount: 0,
          minDistance: 1,
          min: 30,
          max: 500,
          description: 'Auto-rickshaw / meter fare for local transit',
          requiresReceipt: false,
          isActive: true
        },
        {
          mode: 'TAXI',
          rateType: 'PER_KM',
          pricePerKm: 15.0,
          flatAmount: 0,
          minDistance: 5,
          min: 100,
          max: 1200,
          description: 'Cab / Taxi / Ride-hailing for outstation or urgent tasks',
          requiresReceipt: true,
          isActive: true
        },
        {
          mode: 'BUS',
          rateType: 'FLAT',
          pricePerKm: 0,
          flatAmount: 50,
          minDistance: 0,
          min: 50,
          max: 200,
          description: 'City bus / public transit flat reimbursement',
          requiresReceipt: false,
          isActive: true
        },
        {
          mode: 'TRAIN',
          rateType: 'FLAT',
          pricePerKm: 0,
          flatAmount: 80,
          minDistance: 0,
          min: 80,
          max: 300,
          description: 'Local train / metro rail ticket reimbursement',
          requiresReceipt: true,
          isActive: true
        }
      ]

      if (markDirty) {
        this.handleFieldChange()
        this.$toast.info('Standard travel mode presets loaded. Click "Save Settings" to persist.')
      }
    },

    isPerKmMode(item) {
      if (!item) return false
      return item.rateType === 'PER_KM' || (!item.rateType && item.pricePerKm > 0)
    },
    isFlatMode(item) {
      if (!item) return false
      return item.rateType === 'FLAT' || (!item.rateType && !item.pricePerKm && (item.flatAmount || item.min))
    },

    getRateTypeLabel(item) {
      if (this.isPerKmMode(item)) return '₹/KM Rate'
      if (this.isFlatMode(item)) return 'Flat Amount'
      return 'Hybrid'
    },
    getRateTypeIcon(item) {
      if (this.isPerKmMode(item)) return 'mdi-map-marker-distance'
      if (this.isFlatMode(item)) return 'mdi-ticket-percent-outline'
      return 'mdi-swap-horizontal'
    },
    getRateTypeIconColor(item) {
      if (this.isPerKmMode(item)) return '#059669'
      if (this.isFlatMode(item)) return '#d97706'
      return '#2563eb'
    },
    getRateTypeBadgeClass(item) {
      if (this.isPerKmMode(item)) return 'bg-emerald-50 text-emerald-800 border border-emerald-200'
      if (this.isFlatMode(item)) return 'bg-amber-50 text-amber-800 border border-amber-200'
      return 'bg-blue-50 text-blue-800 border border-blue-200'
    },

    formatModeName(mode) {
      if (!mode) return ''
      const match = this.standardCatalog.find((c) => c.value === mode)
      if (match) return match.text
      return mode
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
    },

    getModeMeta(mode) {
      const match = this.standardCatalog.find((c) => c.value === mode)
      if (match) {
        return {
          icon: match.icon,
          color: match.color,
          bgColor: match.bgColor
        }
      }
      return {
        icon: 'mdi-car',
        color: '#475569',
        bgColor: '#f1f5f9'
      }
    },

    formatAmount(amount) {
      if (amount === undefined || amount === null || isNaN(amount)) return '0'
      return Number(amount).toLocaleString('en-IN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      })
    },

    formatMinDistance(distance) {
      return distance ? `${distance} km` : 'None'
    },

    formatFlatAmount(item) {
      if (!item) return '₹ 0'
      const amount = item.max || item.flatAmount || 0
      return `Max ₹ ${this.formatAmount(amount)}`
    },

    formatMaxCap(max) {
      return max ? `₹ ${this.formatAmount(max)}` : 'No cap'
    },

    formatSimRate(item) {
      if (!item) return ''
      if (this.isPerKmMode(item)) {
        return `₹ ${this.formatAmount(item.pricePerKm)}/km`
      }
      return `Max ₹ ${this.formatAmount(item.max || item.flatAmount || 0)}`
    },

    formatSimDistanceBreakdown(mode, distance) {
      return `${distance} km @ ₹ ${this.formatAmount(mode?.pricePerKm || 0)}/km`
    },

    openAddDialog() {
      this.isEditingMode = false
      this.editingIndex = -1
      this.modeForm = {
        mode: '',
        rateType: 'PER_KM',
        pricePerKm: 5,
        flatAmount: 150,
        minDistance: 0,
        min: 0,
        max: null,
        description: '',
        requiresReceipt: false,
        isActive: true
      }
      this.modeDialog = true
    },

    handleModeSelect(modeValue) {
      if (this.isEditingMode) return
      const catalogItem = this.standardCatalog.find((c) => c.value === modeValue)
      if (catalogItem) {
        this.modeForm.rateType = catalogItem.defaultType || 'PER_KM'
        if (catalogItem.defaultType === 'FLAT') {
          this.modeForm.flatAmount = catalogItem.defaultFlat || 150
          this.modeForm.max = catalogItem.defaultFlat || 150
          this.modeForm.min = 0
        } else {
          this.modeForm.pricePerKm = catalogItem.defaultRate || 5
        }
      }
    },

    editMode(item, idx) {
      this.isEditingMode = true
      this.editingIndex = idx
      const isPerKm = this.isPerKmMode(item)
      const flatMax = item.flatAmount || item.max || item.min || 0
      this.modeForm = {
        mode: item.mode,
        rateType: item.rateType || (isPerKm ? 'PER_KM' : 'FLAT'),
        pricePerKm: item.pricePerKm || 0,
        flatAmount: flatMax,
        minDistance: item.minDistance || 0,
        min: isPerKm ? (item.min || 0) : 0,
        max: isPerKm ? (item.max || null) : (flatMax || null),
        description: item.description || '',
        requiresReceipt: item.requiresReceipt === true,
        isActive: item.isActive !== false
      }
      this.modeDialog = true
    },

    closeModeDialog() {
      this.modeDialog = false
    },

    submitModeForm() {
      if (!this.isModeFormValid) return

      const isFlat = this.modeForm.rateType === 'FLAT'
      const flatMaxAmount = isFlat ? Number(this.modeForm.flatAmount) || 0 : 0
      const payload = {
        mode: this.modeForm.mode,
        rateType: this.modeForm.rateType,
        pricePerKm: this.modeForm.rateType === 'PER_KM' ? Number(this.modeForm.pricePerKm) : 0,
        flatAmount: flatMaxAmount,
        minDistance: Number(this.modeForm.minDistance) || 0,
        min: isFlat ? 0 : Number(this.modeForm.min) || 0,
        max: isFlat ? flatMaxAmount : (this.modeForm.max ? Number(this.modeForm.max) : null),
        description: this.modeForm.description || '',
        requiresReceipt: this.modeForm.requiresReceipt === true,
        isActive: this.modeForm.isActive !== false
      }

      if (this.isEditingMode && this.editingIndex > -1) {
        this.$set(this.travelList, this.editingIndex, payload)
        this.$toast.success(`${this.formatModeName(payload.mode)} updated`)
      } else {
        // Check for duplicate
        const exists = this.travelList.find((m) => m.mode === payload.mode)
        if (exists) {
          this.$toast.warning(`${this.formatModeName(payload.mode)} is already configured. Edited instead.`)
          const existingIdx = this.travelList.findIndex((m) => m.mode === payload.mode)
          this.$set(this.travelList, existingIdx, payload)
        } else {
          this.travelList.push(payload)
          this.$toast.success(`${this.formatModeName(payload.mode)} added`)
        }
      }

      this.closeModeDialog()
      this.handleFieldChange()
    },

    confirmDeleteMode(index) {
      const mode = this.travelList[index]
      const name = this.formatModeName(mode?.mode)
      if (confirm(`Remove "${name}" from configured travel modes?`)) {
        this.travelList.splice(index, 1)
        this.$toast.info(`${name} removed`)
        this.handleFieldChange()
      }
    },

    addAllowanceRow() {
      this.allowanceList.push({
        type: 'IN_STATION_WORK',
        amount: 150
      })
      this.handleFieldChange()
    },

    removeAllowanceRow(index) {
      this.allowanceList.splice(index, 1)
      this.handleFieldChange()
    },

    handleFieldChange() {
      // Notify parent index.vue so the parent's Update button becomes active
      this.$emit('travelData', this.travelList)
      this.$emit('allowanceData', this.allowanceList)
      this.$emit('isValueChanged', true)
    },

    // Method mapped to handleUpdateClick in parent OrgConfigComponents/index.vue
    async saveClaimSettings() {
      this.isSaving = true
      try {
        const payload = {
          travelModes: this.travelList.map((m) => ({
            mode: m.mode,
            name: this.formatModeName(m.mode),
            rateType: m.rateType || 'PER_KM',
            pricePerKm: Number(m.pricePerKm) || 0,
            flatAmount: Number(m.flatAmount) || 0,
            min: Number(m.min) || 0,
            max: m.max ? Number(m.max) : null,
            minDistance: Number(m.minDistance) || 0,
            requiresReceipt: m.requiresReceipt === true,
            description: m.description || '',
            isActive: m.isActive !== false,
            icon: this.getModeMeta(m.mode).icon
          })),
          allowances: this.allowanceList.map((a) => ({
            type: a.type,
            name: a.name || a.type,
            amount: Number(a.amount) || 0,
            requiresReceipt: a.requiresReceipt === true,
            description: a.description || '',
            isActive: a.isActive !== false
          })),
          generalPolicies: {
            autoGpsDistance: this.claimPolicies.autoGpsDistance !== false,
            receiptThreshold: Number(this.claimPolicies.receiptThreshold) || 500,
            maxDailyClaimCap: Number(this.claimPolicies.maxDailyClaimCap) || 5000,
            requireManagerApproval: this.claimPolicies.requireManagerApproval !== false,
            submissionWindowDays: Number(this.claimPolicies.submissionWindowDays) || 30,
            allowPastDateClaims: this.claimPolicies.allowPastDateClaims !== false,
            maxPastDaysAllowed: Number(this.claimPolicies.maxPastDaysAllowed) || 60
          }
        }

        await saveClaimSettings(payload)
        this.$toast.success('Claim & travel settings saved successfully')

        // Sync with parent
        this.$emit('travelData', this.travelList)
        this.$emit('allowanceData', this.allowanceList)
        this.$emit('isValueChanged', false)
        this.$emit('refetchData')
      } catch (error) {
        console.error('Error saving claim settings:', error)
        this.$toast.error(error.response?.data?.message || 'Failed to save claim settings')
      } finally {
        this.isSaving = false
      }
    }
  }
}
</script>

<style scoped>
.claim-settings-container {
  color: #1e293b;
}

.custom-settings-tabs>>>.v-tab {
  border-radius: 8px 8px 0 0;
  margin-right: 4px;
}

.custom-settings-tabs>>>.v-tabs-slider {
  height: 3px;
  border-radius: 3px 3px 0 0;
}

.mode-card {
  min-height: 220px;
}
</style>
